import { useState, useEffect } from 'react';

/**
 * Hook to detect if a media query matches
 * @param query - Media query string (e.g., '(max-width: 767px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // Check if window is defined (SSR safety)
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    // Check if window is defined (SSR safety)
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    // Update state when media query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value
    setMatches(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

/**
 * Hook to detect mobile screens (<768px)
 * @returns boolean indicating if current viewport is mobile
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

// Touch layout covers phones and portrait tablets — anything below Tailwind's
// `lg` breakpoint (1024px), matching the layout switch used across detail views.
const TOUCH_LAYOUT_MAX_WIDTH_PX = 1023;

/**
 * Hook to detect narrow/touch layouts (phones and portrait tablets, <1024px).
 * Used to swap dense desktop tables for touch-friendly entry screens.
 * @returns boolean indicating if current viewport should use the touch layout
 */
export function useIsTouchLayout(): boolean {
  return useMediaQuery(`(max-width: ${TOUCH_LAYOUT_MAX_WIDTH_PX}px)`);
}
